import { appDispatch } from 'state'
import { setFarmsUserData } from 'state/farms'
import { fetchFarmsUser } from 'state/farms/fetchFarmsUser'
import { getAccount, getSigner, onAccountInitOrChange } from 'state/web3/account'
import createDependableResource from 'utils/zustand/createDependableResource'
import { useDeserializedFarms, getDeserializedFarm, useDeserializedFarmFromPid } from 'state/farms/useDeserializedFarms'
import { farmsConfig } from 'config/constants'
import { getAddress } from 'utils/addressHelpers'
import { DeserializedFarm, DeserializedFarmUserData } from 'state/types'
import { immediatePromise } from 'utils/waitPromise'

const activeFarmPids = farmsConfig.filter((farm) => !farm.isFinished).map((f) => f.pid)
const finishedFarmPids = farmsConfig.filter((farm) => farm.isFinished).map((f) => f.pid)

export const useActiveFarmsUserDataFetching = createDependableResource<boolean>({
  initValue: false,
  getValue: async () => {
    const account = getAccount()
    if (!account) return false
    const signer = getSigner()

    appDispatch(setFarmsUserData(await fetchFarmsUser({ pids: activeFarmPids, account, signer })))
    return true
  },
  onInit: ({ resetValue, refresh, getIsStarted }) => {
    onAccountInitOrChange(() => {
      if (!getIsStarted()) return
      resetValue()
      refresh()
    })
  },
  dependResources: [],
  dbgName: 'ActiveFarmsUserLoading',
})

export const useFinishedFarmsUserDataFetching = createDependableResource<boolean>({
  initValue: false,
  getValue: async () => {
    const account = getAccount()
    if (!account) return false
    const signer = getSigner()

    appDispatch(setFarmsUserData(await fetchFarmsUser({ pids: finishedFarmPids, account, signer })))
    return true
  },
  onInit: ({ resetValue, refresh, getIsStarted }) => {
    onAccountInitOrChange(() => {
      if (!getIsStarted()) return
      resetValue()
      refresh()
    })
  },
  dependResources: [],
  dbgName: 'FinishedFarmsUserLoading',
})

export const getLoadedDeserializedFarmUserDataFromPid = async (
  pid: number,
  version: number,
): Promise<DeserializedFarmUserData> => {
  const deserializedFarm = getDeserializedFarm(pid, version)
  if (deserializedFarm.userData?.loaded) return deserializedFarm.userData

  if (!deserializedFarm.isFinished) {
    await useActiveFarmsUserDataFetching.getState().getLoadedValue()
  } else {
    await useFinishedFarmsUserDataFetching.getState().getLoadedValue()
  }
  await immediatePromise()
  return getDeserializedFarm(pid, version).userData
}

export const useDeserializedFarmUserFromPid = (pid: number, version: number): DeserializedFarmUserData => {
  const farm = useDeserializedFarmFromPid(pid, version)
  if (farm) {
    if (!farm.isFinished) {
      useActiveFarmsUserDataFetching.start()
    } else {
      useFinishedFarmsUserDataFetching.start()
    }
  }
  return farm.userData
}

export const refreshFarmUserData = async (deserializedFarm: DeserializedFarm): Promise<void> => {
  if (!deserializedFarm.isFinished) {
    await useActiveFarmsUserDataFetching.getState().refresh()
  } else {
    await useFinishedFarmsUserDataFetching.getState().refresh()
  }
  await immediatePromise()
}

export const refreshFarmUserDataFromPid = async (pid: number, version: number): Promise<DeserializedFarmUserData> => {
  const deserializedFarm = getDeserializedFarm(pid, version)
  await refreshFarmUserData(deserializedFarm)
  return getDeserializedFarm(pid, version).userData
}

export const refreshFarmUserDataFromLpAddr = async (lpAddr: string): Promise<DeserializedFarmUserData> => {
  const deserializedFarm = useDeserializedFarms
    .getState()
    .value.farms.find((farm) => getAddress(farm.lpAddresses).toLowerCase() === lpAddr.toLowerCase())
  await refreshFarmUserData(deserializedFarm)
  return getDeserializedFarm(deserializedFarm.pid, deserializedFarm.version).userData
}
